import React, { useEffect } from "react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";

import useAnimatedNavToggler from "../../helpers/useAnimatedNavToggler.js";
const MENU_ICON_SRC = "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/LOGO%20for%20website/grain%20icon/Mask%20group%20%283%29.png";
const CLOSE_ICON_SRC = "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/LOGO%20for%20website/grain%20icon/Group%2019.png";

/* ====== 顶栏（全时移动布局）====== */
const Bar = styled.header`
    ${tw`w-full`}
    background: ${(p) => p.$background || "#FFFBDC"};
`;

const BarInner = styled.div`
    ${tw`mx-auto flex items-center`}
    padding-left: clamp(16px, 4vw, 32px);
    padding-right: clamp(16px, 4vw, 32px);
    padding-top: 10px;
    padding-bottom: 10px;
    min-height: 48px;
    position: relative;

    @media (min-width: 768px) {
        display: grid;
        grid-template-columns: 1fr auto 1fr;
        align-items: center;
        gap: clamp(16px, 4vw, 48px);
        padding-top: 0;
        padding-bottom: 0;
        height: 64px;
    }
`;

const Left = styled.div`
    ${tw`flex items-center`}

    @media (min-width: 768px) {
        display: none;
    }
`;
const MenuBtn = styled.button.attrs({ type: "button" })`
    ${tw`flex items-center justify-center focus:outline-none`}
    width: 31px;
    height: 31px;
    border-radius: 9999px;
    border: 0;
    padding: 0;
    background-color: transparent;
    transition: transform 0.18s ease;

    &:hover {
        transform: scale(1.05);
    }

    &:active {
        transform: scale(0.95);
    }

    &:focus-visible {
        outline: 2px solid rgba(20, 19, 59, 0.6);
        outline-offset: 4px;
    }

    @media (min-width: 768px) {
        display: none;
    }
`;

const MenuIconImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
`;

const Center = styled.div`
    ${tw`flex items-center justify-center`}
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    height: 100%;
    width: auto;

    @media (min-width: 768px) {
        position: static;
        transform: none;
        height: auto;
        justify-content: center;
        justify-self: center;
    }
`;
const Logo = styled.img`
    height: 32px; width: auto;
`;

const Right = styled.div`
    ${tw`flex items-center`}
    margin-left: auto;

    @media (min-width: 768px) {
        display: none;
    }
`;
const OrderLink = styled.a`
    ${tw`font-semibold`}
    font-family: "Work Sans", system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
    font-weight: 400; font-size: 15px; color: #14133B; text-decoration: none;
    display: inline-flex; align-items: center; gap: 6px; padding: 8px 0;
    &:active { transform: translateY(0.5px); }
`;

const Arrow = styled.span.attrs({ "aria-hidden": "true" })`
    display: inline-block;  font-size: 1.6em; line-height: 1; transform: translateY(1px);
`;

/* ====== 全屏抽屉（从上向下） ====== */
const Drawer = styled(motion.div)`
    ${tw`fixed inset-0 z-50`}
    background: ${(p) => p.$drawerBackground || "#FAB600"};
    display: flex; flex-direction: column;

    /* 统一水平 padding，左右对齐点 */
    --pad-x: clamp(32px, 6vw, 64px);
    padding: max(14px, env(safe-area-inset-top)) var(--pad-x) 24px;
`;

const DrawerHeader = styled.div`
    position: relative;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 4px;
    
`;

/* 左上：关闭（与列表左侧对齐） */
const DrawerClose = styled.button.attrs({ type: "button" })`
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: 0;
    width: 31px;
    height: 31px;
    padding: 0;
    cursor: pointer;
    transition: transform 0.2s ease;

    &:hover {
        transform: translateY(-50%) scale(1.05);
    }

    &:active {
        transform: translateY(-50%) scale(0.95);
    }

    &:focus-visible {
        outline: 2px solid rgba(20, 19, 59, 0.6);
        outline-offset: 4px;
    }
`;

const CloseIconImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
`;

/* 右上：Order ›（与列表右侧对齐） */
const DrawerOrder = styled.a`
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    color: #14133B;
    text-decoration: none;
    font-family: "Work Sans", system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
    font-weight: 400;
    font-size: 15px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 0; /* 触控区 */
`;

/* 顶部 Logo 居中 */
const DrawerLogo = styled.img`
    height: 28px; width: auto;
`;

/* ✅ 抽屉内容：竖排列表 + 深蓝色 */
const DrawerContent = styled.div`
    margin-top: 8px;
    display: flex;
    flex-direction: column;

    a {
        display: block;
        color: #14133B;
        text-decoration: none;
        font-size: 40px;
        line-height: 1.35;
        padding: 16px 0;

        &:hover,
        &:focus-visible {
            text-decoration-line: underline;
            text-decoration-color: #14133B;
            text-decoration-thickness: 2px; /* 下划线粗细 */
            text-underline-offset: 4px; /* 与文字的间距 */
        }
    }

    a:last-of-type {
        border-bottom: 0;
    }

`;

const DesktopNav = styled.nav`
    display: none;

    @media (min-width: 768px) {
        display: flex;
        align-items: center;
        gap: clamp(16px, 2vw, 28px);
        justify-self: start;
    }
`;

const DesktopNavRightWrap = styled.div`
    display: none;

    @media (min-width: 768px) {
        display: flex;
        align-items: center;
        gap: clamp(16px, 2vw, 28px);
        justify-self: end;
    }
`;

const DesktopNavRight = styled.nav`
    display: flex;
    align-items: center;
    gap: clamp(16px, 2vw, 28px);
`;

const DesktopLink = styled.a`
    font-family: "Work Sans", system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
    font-weight: 400;
    font-size: 15px;
    color: #14133B;
    text-decoration: none;
    padding: 4px 0;
    text-transform: uppercase;

    &:hover,
    &:focus-visible {
        text-decoration-line: underline;
        text-decoration-thickness: 2px;
        text-underline-offset: 4px;
    }
`;

const DesktopOrderButton = styled.a`
    font-family: "Work Sans", system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
    font-weight: 300;
    font-size: 15px;
    color: #FAB600;
    text-transform: uppercase;
    text-decoration: none;
    background: #14133B;
    border-radius: 9999px;
    padding: 3px 20px;
    letter-spacing: 0.06em;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(20,19,59,0.1);
    transition: background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease;

    &:hover {
        background: #FFFBDC;
        color: #14133B;
        border-color: #14133B;
    }
`;

const HeaderWrap = styled.div`
    position: sticky;
    top: 0;
    left: 0;
    z-index: 45;
    width: 100%;
    display: block;
`;

const TickerBar = styled.div`
    ${tw`w-full`}
    position: relative;
    background: #FAB600;
    border-top: 1px solid #000000;
    border-bottom: 1px solid  #000000;
    overflow: hidden;
`;

const TickerTrack = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    white-space: normal;
    font-family: "Roboto Condensed", system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
    font-size: clamp(1.0rem, 1.0rem + 0.2vw, 1.4rem);
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: none;
    color: #14133B;
    padding: 5px;
`;

const TickerMessage = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
`;

const TickerLink = styled.a`
    color: #14133B;
    font-weight: 600;
    text-decoration: underline;
    text-decoration-thickness: 1.5px;
    text-underline-offset: 4px;
    transition: opacity 0.2s ease;

    &:hover,
    &:focus-visible {
        opacity: 0.8;
    }
`;

export default function MobileHeaderOnly({
                                             logoSrc = "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/LOGO%20for%20website/Blue.svg",
                                             drawerLogoSrc = "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/LOGO%20for%20website/Blue.svg",
                                             onOrderClick,
                                             orderHref = "https://order.grainmediterranean.com/",
                                             links, // 仍可传自定义 <nav>；样式由 DrawerContent 兜底
                                             headerBg,
                                             drawerBg,
                                             desktopLeftLinks,
                                             desktopRightLinks,
                                         }) {
    const { showNavLinks, toggleNavbar } = useAnimatedNavToggler();

    useEffect(() => {
        const html = document.documentElement;
        const body = document.body;
        if (showNavLinks) {
            const prevHtmlOverflow = html.style.overflow;
            const prevBodyOverflow = body.style.overflow;
            html.style.overflow = "hidden";
            body.style.overflow = "hidden";
            return () => {
                html.style.overflow = prevHtmlOverflow;
                body.style.overflow = prevBodyOverflow;
            };
        }
    }, [showNavLinks]);

    const leftNav = desktopLeftLinks ?? [
        { label: "Menu", href: "/menu" },
        { label: "Our Story", href: "/story" },
        { label: "Our Foundation", href: "/foundation" },
    ];

    const rightNav = desktopRightLinks ?? [
        { label: "Loyalty", href: "https://grainmediterranean.redcatcloud.com.au/universal/register" },
        { label: "Catering", href: "/catering" },
    ];

    const defaultLinks = (
        <>
            <a href="/">Home</a>
            <a href="/menu">Menu</a>
            <a href="/story">Our Story</a>
            <a href="/foundation">Our Foundation</a>
            <a href="https://grainmediterranean.redcatcloud.com.au/universal/register">Loyalty</a>
            <a href="/catering">Catering</a>
        </>
    );

    return (
        <>
            <HeaderWrap>
                <TickerBar aria-label="A free meal for you - Recommend your office now!">
                    <TickerTrack>
                        <TickerMessage>
                            <span>A free meal for you - </span>
                            <TickerLink href="/recommend-office">
                                Recommend your office now
                            </TickerLink>
                            <span>!</span>
                        </TickerMessage>
                    </TickerTrack>
                </TickerBar>

                {/* 顶栏 */}
                <Bar $background={headerBg}>
                    <BarInner>
                        <DesktopNav aria-label="Primary navigation">
                            {leftNav.map((item) => (
                                <DesktopLink key={item.href} href={item.href}>{item.label}</DesktopLink>
                            ))}
                        </DesktopNav>

                        <Left>
                            <MenuBtn aria-label="Open menu" onClick={toggleNavbar}>
                                <MenuIconImg src={MENU_ICON_SRC} alt="" role="presentation" />
                            </MenuBtn>
                        </Left>

                        <Center>
                            <a href="/" aria-label="Go to homepage">
                                <Logo src={logoSrc} alt="Logo" />
                            </a>
                        </Center>

                        <DesktopNavRightWrap>
                            <DesktopNavRight aria-label="Secondary navigation">
                                {rightNav.map((item) => (
                                    <DesktopLink key={item.href} href={item.href}>{item.label}</DesktopLink>
                                ))}
                            </DesktopNavRight>

                            <DesktopOrderButton
                                href={orderHref}
                                onClick={(e) => {
                                    if (onOrderClick) { e.preventDefault(); onOrderClick(); }
                                }}
                            >
                                Order
                            </DesktopOrderButton>
                        </DesktopNavRightWrap>

                        <Right>
                            <OrderLink
                                href={orderHref}
                                onClick={(e) => {
                                    if (onOrderClick) { e.preventDefault(); onOrderClick(); }
                                }}
                            >
                                Order >
                            </OrderLink>
                        </Right>
                    </BarInner>
                </Bar>
            </HeaderWrap>

            {/* 抽屉（从上向下） */}
            <Drawer
                $drawerBackground={drawerBg}
                role="dialog"
                aria-modal="true"
                initial={{ y: "-100%", opacity: 0, display: "none" }}
                animate={
                    showNavLinks
                        ? { y: 0, opacity: 1, display: "flex" }
                        : { y: "-100%", opacity: 0, transitionEnd: { display: "none" } }
                }
                transition={{ duration: 0.28, ease: [0.22, 0.61, 0.36, 1] }}
                onClick={(e) => {
                    const a = e.target.closest("a");
                    if (a) toggleNavbar();
                }}
            >
                <DrawerHeader>
                    <DrawerClose aria-label="Close menu" onClick={toggleNavbar}>
                        <CloseIconImg src={CLOSE_ICON_SRC} alt="" role="presentation" />
                    </DrawerClose>

                    {/* 右上角 Order ›，与左右 padding 对齐 */}
                    <DrawerOrder
                        href={orderHref}
                        onClick={(e) => {
                            if (onOrderClick) { e.preventDefault(); onOrderClick(); }
                            toggleNavbar();
                        }}
                    >
                        Order >
                    </DrawerOrder>

                    <DrawerLogo src={drawerLogoSrc} alt="Logo" />
                </DrawerHeader>

                <DrawerContent>
                    {links ? links : defaultLinks}
                </DrawerContent>
            </Drawer>
        </>
    );
}
